// adapterDisplay — the only way an adapter gets a label in the wizard.
//
// A bank references its adapter by id, and several places used to fall back to
// that id when the adapter's name was not at hand (`name || id`), or handed an
// id to an antd <Select> whose option list did not contain it (antd then
// prints the raw `value`). Either way a UUID reached the screen where the
// adapter's name belongs.
//
// `AdapterDisplayName` is a branded string. Every field that is drawn as an
// adapter label is typed with it, and the only constructor is
// `adapterDisplayName`, which never returns an id: an empty name, or a name
// that is itself a UUID, becomes UNNAMED_ADAPTER_LABEL. A bare `string` --
// including an id -- is a compile error in those fields.

declare const adapterDisplayNameBrand: unique symbol;

export type AdapterDisplayName = string & { readonly [adapterDisplayNameBrand]: true };

export const UNNAMED_ADAPTER_LABEL = "Unnamed adapter";

const UUID_TEXT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The label to draw for an adapter, from whatever name the source carried. */
export function adapterDisplayName(name: unknown): AdapterDisplayName {
  const trimmed = typeof name === "string" ? name.trim() : "";
  const label = trimmed === "" || UUID_TEXT.test(trimmed) ? UNNAMED_ADAPTER_LABEL : trimmed;
  return label as AdapterDisplayName;
}

/** One entry of the bank form's adapter dropdown: an id to select, a name to show. */
export interface AdapterSelectOption {
  value: string;
  label: AdapterDisplayName;
}

interface NamedAdapter {
  id: string;
  name: AdapterDisplayName;
  lift_type?: string;
}

/**
 * The dropdown's options: every adapter matching `liftType`, plus the adapter
 * currently selected even when it is not in `adapters` or does not match the
 * lift type. Without that last entry the selected value has no option and the
 * dropdown prints its id. Its name comes from `knownNames`, else
 * UNNAMED_ADAPTER_LABEL.
 */
export function adapterSelectOptions(
  adapters: readonly NamedAdapter[],
  liftType: string,
  selectedId: string | undefined,
  knownNames: Readonly<Record<string, AdapterDisplayName>>,
): AdapterSelectOption[] {
  const options: AdapterSelectOption[] = adapters
    .filter((ad) => !ad.lift_type || ad.lift_type.toUpperCase() === liftType)
    .map((ad) => ({
      value: ad.id,
      label: adapterDisplayName(ad.lift_type ? `${ad.name} (${ad.lift_type})` : ad.name),
    }));
  if (selectedId && !options.some((o) => o.value === selectedId)) {
    const known = adapters.find((ad) => ad.id === selectedId)?.name ?? knownNames[selectedId];
    options.push({ value: selectedId, label: known ?? adapterDisplayName(undefined) });
  }
  return options;
}
